import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Users, Megaphone } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

const emptyForm = { title: "", body: "", target_users: [], active: true };

export default function AdminAnnouncements() {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [targetMode, setTargetMode] = useState("all"); // 'all' | 'specific'

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([api.get("/announcements"), api.get("/users")]);
      setList(a.data);
      setUsers(u.data.filter((x) => x.role === "pegawai"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openForm = (ann = null) => {
    if (ann) {
      setEditId(ann.id);
      setForm({ title: ann.title, body: ann.body, target_users: ann.target_users || [], active: ann.active });
      setTargetMode((ann.target_users || []).length === 0 ? "all" : "specific");
    } else {
      setEditId(null);
      setForm(emptyForm);
      setTargetMode("all");
    }
    setOpenCreate(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        target_users: targetMode === "all" ? [] : form.target_users,
        active: form.active,
      };
      if (editId) {
        await api.put(`/announcements/${editId}`, payload);
        toast.success("Pengumuman diperbarui");
      } else {
        await api.post("/announcements", payload);
        toast.success("Pengumuman dibuat");
      }
      setOpenCreate(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const del = async (ann) => {
    if (!window.confirm(`Hapus pengumuman "${ann.title}"?`)) return;
    try { await api.delete(`/announcements/${ann.id}`); toast.success("Dihapus"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const toggleUser = (uid) => {
    setForm((f) => {
      const exists = f.target_users.includes(uid);
      return { ...f, target_users: exists ? f.target_users.filter((x) => x !== uid) : [...f.target_users, uid] };
    });
  };

  if (loading) return <PageLoader label="Memuat pengumuman..." />;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto" data-testid="admin-announcements">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <p className="overline">Komunikasi</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Pengumuman</h1>
          <p className="text-sm text-zinc-500 mt-1">Tampil di halaman utama portal pegawai</p>
        </div>
        <Button onClick={() => openForm()} data-testid="btn-add-ann"><Plus className="w-4 h-4 mr-1" /> Tambah Pengumuman</Button>
      </div>

      {list.length === 0 ? (
        <Card className="p-12 text-center border border-zinc-200 shadow-none">
          <Megaphone className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
          <p className="text-sm text-zinc-500">Belum ada pengumuman</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <Card key={a.id} className={`p-4 border ${a.active ? "border-zinc-200" : "border-zinc-200 opacity-60"} shadow-none`} data-testid={`ann-${a.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={a.active ? "default" : "secondary"} className="text-[0.65rem]">
                      {a.active ? "AKTIF" : "NONAKTIF"}
                    </Badge>
                    <Badge variant="outline" className="text-[0.65rem]">
                      <Users className="w-3 h-3 mr-1" />
                      {a.target_users.length === 0 ? "Semua Pegawai" : `${a.target_users.length} Pegawai`}
                    </Badge>
                  </div>
                  <h3 className="font-display font-semibold text-base">{a.title}</h3>
                  {a.body && <p className="text-sm text-zinc-600 mt-1 whitespace-pre-line line-clamp-3">{a.body}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openForm(a)} data-testid={`btn-edit-ann-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(a)} className="text-red-600" data-testid={`btn-del-ann-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Pengumuman" : "Pengumuman Baru"}</DialogTitle>
            <DialogDescription>Pengumuman akan tampil di halaman utama portal pegawai</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Judul *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Contoh: Libur Lebaran" data-testid="ann-title" />
            </div>
            <div>
              <Label className="text-xs">Isi Pengumuman</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} placeholder="Detail pengumuman..." data-testid="ann-body" />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Target Penerima</Label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button type="button" onClick={() => setTargetMode("all")} className={`p-2 rounded-md border text-sm text-left ${targetMode === "all" ? "border-primary bg-blue-50" : "border-zinc-200"}`} data-testid="target-all">
                  <p className="font-medium">Semua Pegawai</p>
                  <p className="text-xs text-zinc-500">{users.length} orang</p>
                </button>
                <button type="button" onClick={() => setTargetMode("specific")} className={`p-2 rounded-md border text-sm text-left ${targetMode === "specific" ? "border-primary bg-blue-50" : "border-zinc-200"}`} data-testid="target-specific">
                  <p className="font-medium">Pilih Pegawai</p>
                  <p className="text-xs text-zinc-500">{form.target_users.length} dipilih</p>
                </button>
              </div>
              {targetMode === "specific" && (
                <div className="border border-zinc-200 rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-zinc-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.target_users.includes(u.id)} onChange={() => toggleUser(u.id)} data-testid={`target-user-${u.id}`} />
                      <span className="flex-1">{u.name} <span className="text-xs text-zinc-500">({u.employee_id})</span></span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="ann-active" />
              <Label className="text-sm">Aktif (tampil ke pegawai)</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}>Batal</Button>
              <Button type="submit" disabled={busy} data-testid="btn-submit-ann">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
